/**
 * Top of the Space Overview tab.
 *
 *   Row 1 — Total / Open / Completed (sourced from the whole-Space
 *           aggregator). These add up — Total = Open + Completed. Overdue
 *           was dropped because it's a slice of Open, not a separate
 *           bucket, which made the tiles read as if they didn't sum.
 *
 *   Row 2 — Lists / Members / Files & Links (Documents + Files + external
 *           Links rolled into one tile so the row doesn't sprawl).
 *
 * The tiles deliberately mirror the Dashboard's `KpiHeroTile` (rounded-xl
 * card, soft tone halo, large neutral number, tone-coloured icon tile) so
 * the two surfaces share one visual language. The colour comes from the
 * icon tile + halo, NOT the digits — same as the Dashboard.
 */

import { ListTodo, CircleDot, CircleCheck, List, Users, Paperclip } from 'lucide-react'
import { formatNumber } from '../../lib/format'

// Tone palette lifted from the Dashboard's KpiHeroTile so the cards match
// pixel-for-pixel. `tile` colours the icon chip; `halo` is the blurred
// gradient blob behind the top-right corner.
const TILE_TONES = {
  sky: { tile: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10', halo: 'from-[#DBEAFE] to-transparent dark:from-transparent' },
  violet: { tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10', halo: 'from-[#EDE9FE] to-transparent dark:from-transparent' },
  emerald: { tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10', halo: 'from-[#D1FAE5] to-transparent dark:from-transparent' },
  amber: { tile: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-white/[0.06] dark:text-orange-300 dark:border-white/10', halo: 'from-[#FFEDD5] to-transparent dark:from-transparent' },
  gold: { tile: 'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10', halo: 'from-[#FEF3C7] to-transparent dark:from-transparent' },
  pink: { tile: 'bg-[#FCE7F3] text-[#DB2777] border border-[#FBCFE8] dark:bg-white/[0.06] dark:text-pink-300 dark:border-white/10', halo: 'from-[#FCE7F3] to-transparent dark:from-transparent' },
}

export function SpaceOverviewKpiStrip({ overview }) {
  const { tasks, lists, members, documents, files, externalLinks } = overview
  const assetsTotal =
    (documents?.total ?? 0) + (files?.total ?? 0) + (externalLinks?.total ?? 0)
  // When one List is in scope, every tile here is that List's figure EXCEPT
  // Members — there is no per-List membership, so the roster is the Space's
  // people. Saying so on the tile stops it reading as a wrong List count
  // beside the List-scoped Workload panel below.
  const scopedToList = Boolean(overview?.scope?.listId)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Total = Open + Completed; Overdue lives on the List / Board
         *  views (it's a slice of Open, not a separate bucket). */}
        <KpiTile label="Total tasks" value={tasks.total} icon={ListTodo} tone="sky" />
        <KpiTile label="Open" value={tasks.open} icon={CircleDot} tone="violet" />
        <KpiTile label="Completed" value={tasks.completed} icon={CircleCheck} tone="emerald" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiTile label="Lists" value={lists.total} icon={List} tone="amber" />
        <KpiTile
          label={scopedToList ? 'Space members' : 'Members'}
          value={members.total}
          icon={Users}
          tone="gold"
        />
        <KpiTile label="Files & Links" value={assetsTotal} icon={Paperclip} tone="pink" />
      </div>
    </div>
  )
}

function KpiTile({ label, value, icon: Icon, tone = 'sky' }) {
  const tones = TILE_TONES[tone] ?? TILE_TONES.sky
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0 leading-tight">
            {label}
          </h3>
          <p className="mt-2 font-display text-4xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
            {typeof value === 'number' ? formatNumber(value) : value}
          </p>
        </div>
        {Icon ? (
          <div
            className={
              'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
              tones.tile
            }
          >
            <Icon size={24} />
          </div>
        ) : null}
      </div>
    </div>
  )
}
